import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { ILicense } from '../interfaces/license.interface';

/**
 * Servicio encargado de gestionar la información y validación de licencias en la aplicación.
 *
 * Este servicio incluye funcionalidades como la verificación de licencias, activación, renovación
 * y consulta del estado de las licencias.
 *
 * `@Injectable` permite la inyección de este servicio en otros componentes o servicios.
 * `providedIn: 'root'` garantiza que el servicio esté disponible en toda la aplicación como un singleton,
 * lo que significa que solo habrá una instancia de este servicio a lo largo de la app.
 */
@Injectable({
  providedIn: 'root',
})
export class LicenseService {
  /**
   * URL del endpoint para acceder a las licencias.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para obtener la lista de licencias disponibles en la aplicación.
   */
  private url: string = '/licenses';

  /**
   * Constructor de la clase LicenseService.
   *
   * Inyecta el servicio `HttpClient`, que se utiliza para realizar solicitudes HTTP
   * al servidor de la API y gestionar las operaciones relacionadas con las licencias.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene una lista de licencias con filtros opcionales.
   *
   * @param productId - Filtra las licencias por un ID de producto específico. `null` para ignorar este filtro.
   * @param disabled - Filtra las licencias por estado (habilitada/deshabilitada). `null` para no aplicar el filtro.
   * @param offset - Define el desplazamiento para la paginación. `null` si no se usa paginación.
   * @param limit - Establece el número máximo de licencias a devolver. `null` para no limitar los resultados.
   *
   * @returns Una promesa que se resuelve con la lista de licencias obtenidas.
   */
  public getAll(
    productId: number | null = null,
    disabled: number | null = null,
    offset: number | null = null,
    limit: number | null = null
  ): Promise<any> {
    const queryParams = { productId, disabled, offset, limit };
    const params = Object.entries(queryParams).reduce(
      (acc, [key, value]) =>
        value !== null ? acc.set(key, value.toString()) : acc,
      new HttpParams()
    );

    return firstValueFrom(
      this.http.get(environment.apiUrl + this.url, { params })
    );
  }

  /**
   * Método para crear un nuevo registro de licencia en la API.
   * Realiza una solicitud HTTP POST para enviar los datos de la licencia al servidor.
   *
   * @param license - Objeto que contiene la información de la licencia a ser creado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la creación de la licencia.
   */
  create(license: ILicense): Promise<any> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + this.url, {
        productId: license.product.id,
        alias: license.alias,
        name: license.name,
        type: license.type,
        disabled: license.disabled,
      })
    );
  }

  /**
   * Método para actualizar un registro de la licencia en la API.
   * Realiza una solicitud HTTP PUT para enviar los datos actualizados de la licencia al servidor.
   *
   * @param license - Objeto que contiene la información de la licencia con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la actualización de la licencia.
   */
  update(license: ILicense): Promise<any> {
    return firstValueFrom(
      this.http.put(`${environment.apiUrl + this.url}/${license.id}`, {
        productId: license.product.id,
        alias: license.alias,
        name: license.name,
        type: license.type,
        disabled: license.disabled,
      })
    );
  }

  /**
   * Método para eliminar un registro de la licencia en la API.
   * Realiza una solicitud HTTP DELETE para eliminar la licencia basado en su ID.
   *
   * @param license - Objeto que contiene la información de la licencia con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la eliminación de la licencia.
   */
  delete(license: ILicense): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${environment.apiUrl + this.url}/${license.id}`)
    );
  }
}
